import { useState, useEffect } from 'react';
import { Search, Database, Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { apiClient } from '@/api/client';
import { formatTimestamp } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { MemoryChunk } from '@/types';
import { useParams, useNavigate } from 'react-router-dom';

export function Memory() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const [memory, setMemory] = useState<MemoryChunk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newSource, setNewSource] = useState('assistant');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    if (sessionId) {
      loadMemory();
    }
  }, [sessionId]);

  const loadMemory = async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      const res = await apiClient.getMemory(sessionId);
      setMemory(res.memory || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load memory');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!sessionId || !newContent.trim()) return;
    try {
      // Note: This would need a POST endpoint for adding memory
      // For now, just close modal
      setShowAddModal(false);
      setNewContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add memory');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this memory chunk?')) return;
    try {
      // Note: This would need a DELETE endpoint
      setMemory(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete memory');
    }
  };

  const handleEdit = (chunk: MemoryChunk) => {
    setEditingId(chunk.id);
    setEditContent(chunk.content);
  };

  const handleSaveEdit = async () => {
    if (editingId === null || !editContent.trim()) return;
    try {
      // Note: This would need a PUT endpoint
      setMemory(prev => prev.map(m => 
        m.id === editingId ? { ...m, content: editContent } : m
      ));
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update memory');
    }
  };

  const filteredMemory = memory.filter(m => 
    m.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.source.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!sessionId) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6 bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 text-center">
        <Database className="h-16 w-16 mx-auto mb-4 text-gray-400" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Select a Session</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Choose a session from the Sessions page to view its memory</p>
        <Button onClick={() => navigate('/sessions')}>Browse Sessions</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Memory</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Session: {sessionId.slice(0, 8)}...</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadMemory} loading={loading}>Refresh</Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Memory
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search memory..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Memory List */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden">
        {error && (
          <div className="p-4 text-red-600 dark:text-red-400 border-b border-gray-200 dark:border-dark-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : filteredMemory.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No memory chunks yet</p>
            <p className="text-sm mt-1">Add memory chunks to give Anika context</p>
            <Button className="mt-4" onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add First Memory
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-dark-700">
            {filteredMemory.map((chunk) => (
              <div key={chunk.id} className="p-6 hover:bg-gray-50 dark:hover:bg-dark-800/50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-dark-700 rounded capitalize">
                        {chunk.source}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Added {formatTimestamp(chunk.created_at)}
                        {chunk.updated_at !== chunk.created_at && (
                          <> • Updated {formatTimestamp(chunk.updated_at)}</>
                        )}
                      </span>
                    </div>
                    <div className="prose dark:prose-invert max-w-none text-sm">
                      {editingId === chunk.id ? (
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full px-3 py-2 border border-primary-500 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 resize-none min-h-[80px]"
                          rows={4}
                        />
                      ) : (
                        <pre className="whitespace-pre-wrap text-gray-900 dark:text-white">{chunk.content}</pre>
                      )}
                    </div>
                    {chunk.metadata && Object.keys(chunk.metadata).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">Metadata</summary>
                        <pre className="mt-1 text-xs bg-gray-100 dark:bg-dark-800 p-2 rounded overflow-x-auto">
                          {JSON.stringify(chunk.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(chunk)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {editingId === chunk.id ? (
                      <>
                        <Button variant="ghost" size="icon" onClick={handleSaveEdit}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setEditingId(null); setEditContent(''); }}>
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(chunk.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Memory Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white dark:bg-dark-900 rounded-xl max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Memory Chunk</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source</label>
                <select
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="assistant">Assistant</option>
                  <option value="user">User</option>
                  <option value="system">System</option>
                  <option value="external">External</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content</label>
                <textarea
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Enter memory content..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-dark-700 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowAddModal(false); setNewContent(''); }}>Cancel</Button>
              <Button onClick={handleAdd} loading={loading}>Add Memory</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}